//Global imports
import {
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CForm,
    CFormInput,
    CFormLabel,
    CFormSelect,
    CFormSwitch,
    CFormTextarea,
} from '@coreui/react';
import React, { useEffect, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

//File imports
import { APP_LANGAUGE, LANGAUGE_NAME } from '../../Constant';
import { Language } from '../../language/English';
import Notification from '../../services/Notification/Notification';
import { DocumentTemplateTranslationService } from './Api/DocumentTemplateTranslationService';
import Logger from '../../services/Logger/Logger';
import { DocumentTemplateTranslationPojo } from './Api/DocumentTemplateTranslationPojo';
import {
    API_DESCRIPTION_EDITOR_IMAGE_UPLOAD,
    DOCUMENT_TEMPLATE_TRANSLATION_STATUS_ACTIVE,
    DOCUMENT_TEMPLATE_TRANSLATION_STATUS_INACTIVE,
} from './Api/DocumentTemplateTranslationConstants';
import CTABox from '../../assets/images/cta_box.webp';
import HowTo from '../../assets/images/how_it_works.webp';
import {
    manageFaqPosition,
    setInitialFaq,
    updateFaqIdWithNote,
} from './Components/FAQs/Redux/faqsSlice';
import {
    clearPreviewImage,
    setPositionPreviewImage,
} from './Components/UploadPreviewImages/Redux/previewImagesSlice';
import { appDispatch, RootState } from '../../app/store';

//Component imports
import { FAQs, UploadPreviewImages } from './Components';
import Loader from '../../components/Layout/Loader';
import { Layout } from '../../components/Layout';
import { HintText, LengthChecker } from '../../components';
import { set } from 'react-hook-form';

interface FormErrors {
    documentTitle?: string;
    url?: string;
    metaTitle?: string;
    metaKeyword?: string;
    metaDescription?: string;
    youtubeLink?: string;
    documentText?: string;
    documentTextPreviewRequired?: string;
    duplicatePlaceholders?: string;
    shortDescription?: string;
    description?: string;
    faqs?: string;
    sourceDocumentTemplateTranslationId?: string;
}

const DocumentTemplateTranslation = () => {
    const editor = useRef(null);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const location = useLocation();
    const { id, language, isEdit, name } = location.state;
    const [Editorconfig, setEditorconfig] = useState({
        height: 900,
    });
    const notification = new Notification();
    const dispatch = useDispatch<appDispatch>();
    const documentTemplateService = new DocumentTemplateTranslationService();
    const logger = new Logger();
    const navigate = useNavigate();
    const faqs = useSelector((state: RootState) => state.faqsReducer.faqs);

    const previewImages = useSelector(
        (state: RootState) => state.previewImages.previewImages,
    );

    const [formData, setFormData] = useState<any>({
        documentTitle: '',
        url: '',
        metaTitle: '',
        metaKeyword: '',
        metaDescription: '',
        youtubeLink: '',
        documentText: '',
        shortDescription: '',
        description: '',
        status: '',
        useOtherLanguageText: '',
        sourceDocumentTemplateTranslationId: '',
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [translationDataLoading, setTranslationDataLoading] =
        useState<boolean>(false);
    const [
        checkingOtherTranslationLoading,
        setCheckingOtherTranslationLoading,
    ] = useState<boolean>(false);
    const [translationData, setTranslationData] =
        useState<DocumentTemplateTranslationPojo | null>(null);
    const [folderName, setFolderName] = useState<string | null>(null);
    const [otherTranslationData, setOtherTranslationData] = useState<
        number[] | null
    >(null);
    const [isEditDocument, setIsEditDocument] = useState<boolean>(false);
    const [previewImageNote, setPreviewImageNote] = useState<boolean>(false);
    const [showFaqNote, setShowFaqNote] = useState<boolean>(false);

    useEffect(() => {
        setCheckingOtherTranslationLoading(true);
        dispatch(setInitialFaq());
        dispatch(clearPreviewImage());
        checkOtherTranslationData();
    }, []);

    const checkOtherTranslationData = async () => {
        try {
            let payload = {
                documentTemplateId: id,
                languageId: APP_LANGAUGE[language],
            };

            const { success, message, data } =
                await documentTemplateService.isOtherTranslationExists(payload);

            if (success) {
                setOtherTranslationData(data);
            } else {
                notification.error(
                    message ?? Language.UNABLE_TO_FETCH_OTHER_TRANSLATION_DATA,
                );
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setCheckingOtherTranslationLoading(false);
        }
    };

    useEffect(() => {
        if (isEdit) {
            setTranslationDataLoading(true);
            getDocumentTemplateTranslationData();
        }
    }, [isEdit]);

    useEffect(() => {
        const JoditEditorConfig = {
            height: 1000,
            readonly: false,
            askBeforePasteHTML: false,
            addNewLineOnDBLClick: false,
            enableDragAndDropFileToEditor: true,
            useSplitMode: false,
            paragraphize: false,
            cleanHTML: {
                fillEmptyParagraph: false,
            },
            uploader: {
                url: API_DESCRIPTION_EDITOR_IMAGE_UPLOAD,
                insertImageAsBase64URI: false,
                imagesExtensions: ['jpg', 'png', 'jpeg', 'gif', 'webp'],
                headers: { Authorization: `Bearer ${user.token}` },
                filesVariableName: function (t: string) {
                    return 'files[' + t + ']';
                },
                withCredentials: false,
                pathVariableName: 'path',
                format: 'json',
                method: 'POST',
                prepareData: function (formData: any) {
                    setShowLoader(true);
                    formData.append('folderName', folderName);
                    return formData;
                },
                isSuccess: function (e: any) {
                    return e && e.response;
                },
                process: function (resp: any) {
                    return {
                        files: resp.response,
                    };
                },
                error: function (this: any, e: Error) {
                    this.j.e.fire('errorMessage', e.message, 'error', 4000);
                    setShowLoader(false);
                },
                defaultHandlerSuccess: function (this: any, resp: any) {
                    const j = this;
                    if (resp.files && resp.files.length) {
                        const tagName = 'img';
                        resp.files.forEach((data: any) => {
                            setFolderName(data.uniqueFolderName);
                            const elm = j.createInside.element(tagName);
                            elm.setAttribute('src', data.filePath);
                            j.s.insertImage(
                                elm as HTMLImageElement,
                                null,
                                j.o.imageDefaultWidth,
                            );
                        });
                    }
                    setShowLoader(false);
                },
                defaultHandlerError: function (this: any, e: { message: any }) {
                    this.j.e.fire('errorMessage', e.message);
                    setShowLoader(false);
                },
            },
        };

        setEditorconfig(JoditEditorConfig);
    }, [folderName, user.token]);

    const getDocumentTemplateTranslationData = async () => {
        try {
            let payload = {
                documentTemplateId: id,
                languageId: APP_LANGAUGE[language],
            };

            const { success, message, data } =
                await documentTemplateService.get(payload);
            const {
                title,
                url,
                metaTitle,
                metaKeyword,
                metaDescription,
                youtubeVideoLink,
                documentText,
                shortDescription,
                description,
                faq,
                folderName,
                status,
                sourceDocumentTranslationIdForText,
                previewBlurredImages,
            }: DocumentTemplateTranslationPojo = data;
            if (success) {
                setTranslationData(data);
                setFolderName(folderName);
                setFormData({
                    documentTitle: title,
                    url,
                    metaTitle,
                    metaKeyword,
                    metaDescription,
                    youtubeLink: youtubeVideoLink ?? '',
                    documentText,
                    shortDescription,
                    description,
                    status,
                    sourceDocumentTemplateTranslationId:
                        sourceDocumentTranslationIdForText,
                    useOtherLanguageText:
                        sourceDocumentTranslationIdForText != null
                            ? true
                            : false,
                });
                if (
                    faq &&
                    Array.isArray(JSON.parse(faq)) &&
                    JSON.parse(faq).length > 0
                ) {
                    dispatch(manageFaqPosition(JSON.parse(faq)));
                } else {
                    dispatch(manageFaqPosition([]));
                }
                if (
                    previewBlurredImages &&
                    Array.isArray(JSON.parse(previewBlurredImages)) &&
                    JSON.parse(previewBlurredImages).length > 0
                ) {
                    dispatch(
                        setPositionPreviewImage(
                            JSON.parse(previewBlurredImages),
                        ),
                    );
                } else {
                    dispatch(setPositionPreviewImage([]));
                }
                setIsEditDocument(true);
                setPreviewImageNote(false);
                dispatch(updateFaqIdWithNote(null));
                setShowFaqNote(false);
            } else {
                notification.error(
                    message ?? Language.UNABLE_TO_FETCH_TRANSLATION_DATA,
                );
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setTranslationDataLoading(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === convertToCamelCase(Language.URL)) {
            newValue = newValue.toLowerCase();
        }
        setFormData((prev: any) => ({ ...prev, [name]: newValue }));

        if (errors[name as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        const regexForCheckingCommaSeperatedString =
            /^([A-Za-zÀ-ÖØ-öø-ÿ0-9!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?`~\s]+?)(,\s*[A-Za-zÀ-ÖØ-öø-ÿ0-9!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?`~\s]+?)*$/;
        const regexForCheckingUrl = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9-]+$/;
        // const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?$/;
        const youtubeRegex =
            /^https:\/\/www\.youtube\.com\/embed\/([a-zA-Z0-9_-]{11})$/;

        let isValid = true;

        if (!formData.documentTitle.trim()) {
            newErrors.documentTitle = Language.DOCUMENT_TITLE_REQUIRED;
            isValid = false;
        }

        if (!formData.url.trim()) {
            newErrors.url = Language.URL_REQUIRED;
            isValid = false;
        } else if (!regexForCheckingUrl.test(formData.url)) {
            newErrors.url = Language.URL_SHOULD_NOT_CONTAIN_WHITE_SPACE;
            isValid = false;
        }

        if (!formData.metaTitle.trim()) {
            newErrors.metaTitle = Language.GOOGLE_TITLE_REQUIRED;
            isValid = false;
        }

        if (!formData.metaKeyword.trim()) {
            newErrors.metaKeyword = Language.GOOGLE_KEYWORD_REQUIRED;
            isValid = false;
        } else if (
            !regexForCheckingCommaSeperatedString.test(
                formData.metaKeyword.trim(),
            )
        ) {
            newErrors.metaKeyword =
                Language.KEYWORD_SHOULD_CONTAIN_COMMA_SEPERATED;
            isValid = false;
        }

        if (!formData.metaDescription.trim()) {
            newErrors.metaDescription = Language.GOOGLE_DESCRIPTION_REQUIRED;
            isValid = false;
        }

        if (formData.youtubeLink != '') {
            if (!youtubeRegex.test(formData.youtubeLink.trim())) {
                newErrors.youtubeLink = Language.YOUTUBE_LINK_NOT_VALID;
                isValid = false;
            }
        }

        if (formData.useOtherLanguageText) {
            if (
                !formData.sourceDocumentTemplateTranslationId ||
                isNaN(Number(formData.sourceDocumentTemplateTranslationId))
            ) {
                newErrors.sourceDocumentTemplateTranslationId =
                    Language.PLEASE_SELECT_VALID_DOCUMENT_TEMPLATE_TRANSLATION;
                isValid = false;
            }
        } else {
            if (!formData.documentText.trim()) {
                newErrors.documentText = Language.DOCUMENT_TEXT_REQUIRED;
                isValid = false;
            } else if (!formData.documentText.includes('[[PREVIEW_END]]')) {
                newErrors.documentTextPreviewRequired =
                    Language.PREVIEW_END_REQUIRED;
                isValid = false;
            } else {
                const placeholders = [
                    ...formData.documentText.matchAll(/\[\[(.*?)\]\]/g),
                ].map((match) => match[1].toLowerCase());
                const duplicates = placeholders.filter(
                    (item, index) => placeholders.indexOf(item) !== index,
                );
                const uniqueDuplicates = Array.from(new Set(duplicates));
                if (uniqueDuplicates.length > 0) {
                    newErrors.duplicatePlaceholders =
                        Language.DUPLICATE_PLACEHOLDERS +
                        uniqueDuplicates.join(', ');
                }
            }
        }

        if (!formData.shortDescription.trim()) {
            newErrors.shortDescription = Language.SHORT_DESCRIPTION_REQUIRED;
            isValid = false;
        }

        if (!formData.description.trim()) {
            newErrors.description = Language.DESCRIPTION_REQUIRED;
            isValid = false;
        } else {
            const requiredPlaceholders = ['cta_banner', 'document_steps'];
            const descriptionPlaceholders = [
                ...formData.description.matchAll(/\[\[(.*?)\]\]/g),
            ].map((match) => match[1].toLowerCase());

            const counts: Record<string, number> = {};

            for (const placeholder of descriptionPlaceholders) {
                counts[placeholder] = (counts[placeholder] || 0) + 1;
            }

            const missingPlaceholders = requiredPlaceholders.filter(
                (placeholder) => !counts[placeholder],
            );
            if (missingPlaceholders.length > 0) {
                newErrors.description =
                    Language.REQUIRED_PLACEHOLDER_MISSING +
                    ': ' +
                    missingPlaceholders
                        .map((p) => `[[${p.toUpperCase()}]]`)
                        .join(', ');
                isValid = false;
            }

            const duplicatedPlaceholders = Object.entries(counts)
                .filter(([_, count]) => count > 1)
                .map(([key]) => key);

            if (duplicatedPlaceholders.length > 0) {
                newErrors.description =
                    Language.DUPLICATE_PLACEHOLDERS +
                    ': ' +
                    duplicatedPlaceholders
                        .map((p) => `[[${p.toUpperCase()}]]`)
                        .join(', ');
                isValid = false;
            }
        }

        faqs.map((content) => {
            if (content.question === '' || content.answer === '') {
                newErrors.faqs = Language.FAQS_REQUIRED;
                isValid = false;
            }
        });

        setErrors(newErrors);

        const errorFields = Object.keys(newErrors);

        if (errorFields.length > 0) {
            const firstErrorElement = document.getElementsByName(
                errorFields[0],
            )[0];
            if (firstErrorElement) {
                firstErrorElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                firstErrorElement.focus();
            } else if (
                errorFields[0] === 'documentText' ||
                errorFields[0] === 'documentTextPreviewRequired' ||
                errorFields[0] === 'duplicatePlaceholders'
            ) {
                const editorElement = document.getElementById('documentText');
                if (editorElement) {
                    editorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            } else if (errorFields[0] === 'description') {
                const editorElement = document.getElementById('description');
                if (editorElement) {
                    editorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            } else if (
                errorFields[0] === 'sourceDocumentTemplateTranslationId'
            ) {
                const editorElement = document.getElementById(
                    'sourceDocumentTemplateTranslationId',
                );
                if (editorElement) {
                    editorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }

            return false;
        }

        return isValid;
    };

    const resetFieldsAndGoBack = () => {
        setFormData({
            documentTitle: '',
            url: '',
            metaTitle: '',
            metaKeyword: '',
            metaDescription: '',
            youtubeLink: '',
            documentText: '',
            shortDescription: '',
            description: '',
        });
        setTranslationData(null);
        navigate(-1);
    };

    const saveTranslation = async () => {
        setShowLoader(true);
        try {
            let payload: any = {
                ...formData,
                documentTemplateId: id,
                languageId: APP_LANGAUGE[language],
                folderName,
            };

            if (faqs && Array.isArray(faqs) && faqs.length > 0) {
                payload['faq'] = faqs.map((faq) => {
                    if (faq?.image) {
                        return {
                            id: faq?.id,
                            question: faq?.question,
                            answer: faq?.answer,
                            image: faq?.fileName,
                            height: faq?.height,
                            width: faq?.width,
                            fileSizeInKb: faq?.fileSizeInKb,
                        };
                    } else {
                        return {
                            id: faq?.id,
                            question: faq?.question,
                            answer: faq?.answer,
                            image: null,
                        };
                    }
                });
            }

            if (
                previewImages &&
                Array.isArray(previewImages) &&
                previewImages.length > 0
            ) {
                payload['previewBlurredImages'] = previewImages.map((img) => ({
                    image: img.image,
                    height: img.height,
                    width: img.width,
                    fileSizeInKb: img.fileSizeInKb,
                }));
            }

            const { success, message } =
                await documentTemplateService.create(payload);
            if (success) {
                notification.success(message);
                // resetFieldsAndGoBack();
                getDocumentTemplateTranslationData();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const updateTranslation = async () => {
        setShowLoader(true);
        try {
            let payload = {
                ...formData,
                documentTemplateId: id,
                languageId: APP_LANGAUGE[language],
                documentTemplateTranslationId:
                    translationData?.documentTemplateTranslationId,
                folderName,
            };

            if (faqs && Array.isArray(faqs) && faqs.length > 0) {
                payload['faq'] = faqs.map((faq) => {
                    if (faq.image) {
                        return {
                            id: faq?.id,
                            question: faq?.question,
                            answer: faq?.answer,
                            image: faq?.fileName,
                            height: faq?.height,
                            width: faq?.width,
                            fileSizeInKb: faq?.fileSizeInKb,
                        };
                    } else {
                        return {
                            id: faq?.id,
                            question: faq?.question,
                            answer: faq?.answer,
                            image: null,
                        };
                    }
                });
            }

            if (
                previewImages &&
                Array.isArray(previewImages) &&
                previewImages.length > 0
            ) {
                payload['previewBlurredImages'] = previewImages.map((img) => ({
                    image: img.image,
                    height: img.height,
                    width: img.width,
                    fileSizeInKb: img.fileSizeInKb,
                }));
            } else {
                payload['previewBlurredImages'] = null;
            }

            const { success, message } =
                await documentTemplateService.update(payload);
            if (success) {
                notification.success(message);
                // resetFieldsAndGoBack();
                getDocumentTemplateTranslationData();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (isEditDocument) {
            updateTranslation();
        } else {
            saveTranslation();
        }
    };

    //to covert into camelcase by using same value from language file.
    const convertToCamelCase = (str: string): string => {
        return str.toLowerCase().replace(/ (.)/, (_, c) => c.toUpperCase());
    };

    const _checkForUrl = async () => {
        if (translationData?.url === formData.url || formData.url === '') {
            return true;
        } else {
            try {
                const { success, message } =
                    await documentTemplateService.isUrlExists(formData.url);
                if (success) {
                    const newErrors: FormErrors = {};
                    newErrors.url = message;
                    setErrors(newErrors);
                }
            } catch (error) {
                notification.error(Language.SOMETHING_IS_WRONG);
                logger.error(error);
            }
        }
    };

    const changeDocumentTemplateTranslationStatus = async (status: number) => {
        try {
            if (translationData?.documentTemplateTranslationId) {
                const documentTemplateService =
                    new DocumentTemplateTranslationService();
                let response = await documentTemplateService.delete(
                    translationData?.documentTemplateTranslationId,
                    status,
                );
                if (response) {
                    if (status === 1) {
                        notification.success(
                            Language.DOCUMENT_ACTIVED_SUCCESSFULLY,
                        );
                    } else {
                        notification.success(
                            Language.DOCUMENT_INACTIVED_SUCCESSFULLY,
                        );
                    }
                    getDocumentTemplateTranslationData();
                } else {
                    notification.error(
                        Language.CANNOT_CHANGE_DOCUMENT_TEMPLATES_TRANSLATION_STATUS_ERROR,
                    );
                }
            } else {
                notification.error(Language.UNABLE_TO_FETCH_TRANSLATION_DATA);
            }
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(e);
        }
    };

    const _onKeyDown = (e: { key: string; preventDefault: () => void }) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const target = e.target as HTMLTextAreaElement;
        const { name } = target;
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const clean = pasted.replace(/\r?\n|\r/g, ' ');
        setFormData((prev: any) => ({
            ...prev,
            [name]: (prev[name] || '') + clean,
        }));
    };

    const getLanguageNameById = (id: number): string | undefined => {
        const langCode = Object.keys(APP_LANGAUGE).find(
            (key) => APP_LANGAUGE[key] === id,
        );

        return langCode ? LANGAUGE_NAME[langCode] : undefined;
    };

    return (
        <Layout>
            {!translationDataLoading && (
                <>
                    {showLoader && <Loader />}
                    <CCard className="mb-4">
                        <CCardHeader>
                            <strong>
                                {name} / {LANGAUGE_NAME[language]}
                            </strong>
                            <div className="d-flex align-items-center gap-2">
                                <CFormLabel
                                    htmlFor="statusToggle"
                                    className={`mb-0 ${formData.status === 0 ? 'text-dark' : 'text-muted'}`}
                                >
                                    {Language.INACTIVE}
                                </CFormLabel>
                                <CFormSwitch
                                    id="statusToggle"
                                    name="status"
                                    className="mt-1"
                                    checked={formData.status === 1}
                                    onChange={(e) =>
                                        changeDocumentTemplateTranslationStatus(
                                            e.target.checked
                                                ? DOCUMENT_TEMPLATE_TRANSLATION_STATUS_ACTIVE
                                                : DOCUMENT_TEMPLATE_TRANSLATION_STATUS_INACTIVE,
                                        )
                                    }
                                    label=""
                                />
                                <CFormLabel
                                    htmlFor="statusToggle"
                                    className={`mb-0 ${formData.status === 1 ? 'text-dark' : 'text-muted'}`}
                                >
                                    {Language.ACTIVE}
                                </CFormLabel>
                            </div>
                        </CCardHeader>
                        <CCardBody>
                            <CForm onSubmit={handleSubmit}>
                                <CFormLabel
                                    htmlFor={convertToCamelCase(
                                        Language.DOCUMENT_TITLE,
                                    )}
                                >
                                    {Language.DOCUMENT_TITLE +
                                        ' ' +
                                        Language.IN +
                                        ' ' +
                                        LANGAUGE_NAME[language]}
                                </CFormLabel>
                                <CFormInput
                                    value={formData.documentTitle}
                                    invalid={!!errors.documentTitle}
                                    feedback={errors.documentTitle}
                                    type="text"
                                    name={convertToCamelCase(
                                        Language.DOCUMENT_TITLE,
                                    )}
                                    placeholder={
                                        Language.ENTER_DOCUMENT_TITLE +
                                        ' ' +
                                        Language.IN.toLowerCase() +
                                        ' ' +
                                        LANGAUGE_NAME[language].toLowerCase()
                                    }
                                    onChange={handleChange}
                                    maxLength={400}
                                />
                                <LengthChecker
                                    currentLength={
                                        formData.documentTitle.length
                                    }
                                    maxLength={400}
                                />

                                <CFormLabel
                                    htmlFor={convertToCamelCase(Language.URL)}
                                >
                                    {Language.URL}
                                </CFormLabel>
                                <CFormInput
                                    type="text"
                                    placeholder={`Ex. ${name.toLowerCase().split(' ').join('-')}`}
                                    value={formData.url}
                                    invalid={!!errors.url}
                                    feedback={errors.url}
                                    onChange={handleChange}
                                    onBlur={_checkForUrl}
                                    name={convertToCamelCase(Language.URL)}
                                    maxLength={200}
                                />
                                <LengthChecker
                                    currentLength={formData.url.length}
                                    maxLength={200}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.GOOGLE_TITLE,
                                    )}
                                >
                                    {Language.GOOGLE_TITLE}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.META_TITLE_HINT}
                                </HintText>
                                <CFormInput
                                    type="text"
                                    value={formData.metaTitle}
                                    placeholder={Language.ENTER_GOOGLE_TITLE}
                                    onChange={handleChange}
                                    invalid={!!errors.metaTitle}
                                    feedback={errors.metaTitle}
                                    name={convertToCamelCase(
                                        Language.META_TITLE,
                                    )}
                                    maxLength={300}
                                />
                                <LengthChecker
                                    currentLength={formData.metaTitle.length}
                                    maxLength={300}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.GOOGLE_KEYWORD,
                                    )}
                                >
                                    {Language.GOOGLE_KEYWORD}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.META_KEYWORD_HINT}
                                </HintText>
                                <CFormInput
                                    type="text"
                                    placeholder={
                                        Language.META_KEYWORD_PLACEHOLDER
                                    }
                                    onChange={handleChange}
                                    value={formData.metaKeyword}
                                    invalid={!!errors.metaKeyword}
                                    feedback={errors.metaKeyword}
                                    name={convertToCamelCase(
                                        Language.META_KEYWORD,
                                    )}
                                    maxLength={300}
                                />
                                <LengthChecker
                                    currentLength={formData.metaKeyword.length}
                                    maxLength={300}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.GOOGLE_DESCRIPTION,
                                    )}
                                    className="mt-3"
                                >
                                    {Language.GOOGLE_DESCRIPTION}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.META_DESCRIPTION_HINT}
                                </HintText>
                                <CFormTextarea
                                    placeholder={
                                        Language.ENTER_GOOGLE_DESCRIPTION
                                    }
                                    onChange={handleChange}
                                    onPaste={handlePaste}
                                    onKeyDown={_onKeyDown}
                                    value={formData.metaDescription}
                                    invalid={!!errors.metaDescription}
                                    feedback={errors.metaDescription}
                                    style={{ height: 180 }}
                                    name={convertToCamelCase(
                                        Language.META_DESCRIPTION,
                                    )}
                                    maxLength={1000}
                                />
                                <LengthChecker
                                    currentLength={
                                        formData.metaDescription.length
                                    }
                                    maxLength={1000}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.YOUTUBE_LINK,
                                    )}
                                    className="mt-3"
                                >
                                    {Language.YOUTUBE_LINK}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.YOUTUBE_HINT}
                                </HintText>
                                <CFormInput
                                    type="text"
                                    placeholder={
                                        Language.ENTER_YOUTUBE_VIDEO_LINK
                                    }
                                    onChange={handleChange}
                                    value={formData.youtubeLink}
                                    invalid={!!errors.youtubeLink}
                                    feedback={errors.youtubeLink}
                                    name={convertToCamelCase(
                                        Language.YOUTUBE_LINK,
                                    )}
                                />

                                {otherTranslationData &&
                                    otherTranslationData?.length > 0 && (
                                        <div className="d-flex align-items-center mt-3">
                                            <CFormLabel
                                                style={{ marginBottom: 0 }}
                                                htmlFor={convertToCamelCase(
                                                    Language.USE_OTHER_LANGUAGE_TEXT,
                                                )}
                                            >
                                                {
                                                    Language.USE_OTHER_LANGUAGE_TEXT
                                                }
                                            </CFormLabel>
                                            <input
                                                type="checkbox"
                                                checked={
                                                    formData.useOtherLanguageText ||
                                                    false
                                                }
                                                className="ms-2"
                                                onChange={(e) => {
                                                    setFormData({
                                                        ...formData,
                                                        useOtherLanguageText:
                                                            e.target.checked,
                                                        sourceDocumentTemplateTranslationId:
                                                            e.target.checked
                                                                ? formData.sourceDocumentTemplateTranslationId
                                                                : '',
                                                    });
                                                    dispatch(
                                                        clearPreviewImage(),
                                                    );
                                                }}
                                            />
                                        </div>
                                    )}

                                {formData.useOtherLanguageText && (
                                    <>
                                        <div
                                            id="sourceDocumentTemplateTranslationId"
                                            className="d-flex align-items-center mt-3"
                                        >
                                            <CFormLabel
                                                className="mb-0 me-2"
                                                htmlFor={convertToCamelCase(
                                                    Language.WHICH_DOCUMENT_TEMPLATE_TRANSLATION,
                                                )}
                                            >
                                                {
                                                    Language.WHICH_DOCUMENT_TEMPLATE_TRANSLATION
                                                }
                                            </CFormLabel>
                                            <CFormSelect
                                                className="w-auto"
                                                value={
                                                    formData.sourceDocumentTemplateTranslationId ||
                                                    ''
                                                }
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        sourceDocumentTemplateTranslationId:
                                                            e.target.value
                                                                ? parseInt(
                                                                      e.target
                                                                          .value,
                                                                      10,
                                                                  )
                                                                : null,
                                                    })
                                                }
                                            >
                                                <option value="">
                                                    {
                                                        Language.SELECT_DOCUMENT_TEMPLATE_TRANSLATION
                                                    }
                                                </option>
                                                {otherTranslationData &&
                                                    otherTranslationData.map(
                                                        (langId) => (
                                                            <option
                                                                key={langId}
                                                                value={langId}
                                                            >
                                                                {getLanguageNameById(
                                                                    langId,
                                                                )}
                                                            </option>
                                                        ),
                                                    )}
                                            </CFormSelect>
                                        </div>
                                        {errors.sourceDocumentTemplateTranslationId && (
                                            <div className="alert alert-danger">
                                                {
                                                    errors.sourceDocumentTemplateTranslationId
                                                }
                                            </div>
                                        )}
                                    </>
                                )}

                                {!formData.useOtherLanguageText && (
                                    <>
                                        <CFormLabel
                                            style={{ marginBottom: 0 }}
                                            htmlFor={convertToCamelCase(
                                                Language.DOCUMENT_TEXT,
                                            )}
                                            className="mt-3"
                                        >
                                            {Language.DOCUMENT_TEXT}
                                        </CFormLabel>
                                        <HintText marginBottom="10px">
                                            {Language.DOCUMENT_TEXT_HINT}
                                        </HintText>
                                        <div id="documentText">
                                            <JoditEditor
                                                ref={editor}
                                                value={formData.documentText}
                                                config={Editorconfig}
                                                onBlur={(newContent) =>
                                                    setFormData({
                                                        ...formData,
                                                        documentText:
                                                            newContent,
                                                    })
                                                }
                                            />
                                        </div>
                                        {errors.documentText && (
                                            <div className="alert alert-danger">
                                                {
                                                    Language.DOCUMENT_TEXT_REQUIRED
                                                }
                                            </div>
                                        )}
                                        {errors.documentTextPreviewRequired && (
                                            <div className="alert alert-danger">
                                                {Language.PREVIEW_END_REQUIRED}
                                            </div>
                                        )}
                                        {errors.duplicatePlaceholders && (
                                            <div className="alert alert-danger">
                                                {errors.duplicatePlaceholders}
                                            </div>
                                        )}
                                    </>
                                )}

                                {!formData.useOtherLanguageText && (
                                    <UploadPreviewImages
                                        folderName={folderName}
                                        previewImageNoteState={previewImageNote}
                                        setPreviewImageNoteState={(
                                            status: boolean,
                                        ) => {
                                            setPreviewImageNote(status);
                                        }}
                                        titleHtmlFor={convertToCamelCase(
                                            Language.UPLOAD_PREVIEW_IMAGES,
                                        )}
                                        setLoader={(status: boolean) =>
                                            setShowLoader(status)
                                        }
                                        setUniqueFolderName={(name: string) =>
                                            setFolderName(name)
                                        }
                                    />
                                )}

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.SHORT_DESCRIPTION,
                                    )}
                                    className="mt-3"
                                >
                                    {Language.SHORT_DESCRIPTION}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.SHORT_DESCRIPTION_HINT}
                                </HintText>
                                <CFormTextarea
                                    placeholder={
                                        Language.ENTER_SHORT_DESCRIPTION
                                    }
                                    onKeyDown={_onKeyDown}
                                    onChange={handleChange}
                                    onPaste={handlePaste}
                                    value={formData.shortDescription}
                                    invalid={!!errors.shortDescription}
                                    feedback={errors.shortDescription}
                                    maxLength={1000}
                                    style={{ height: 180 }}
                                    name={convertToCamelCase(
                                        Language.SHORT_DESCRIPTION,
                                    )}
                                />
                                <LengthChecker
                                    currentLength={
                                        formData.shortDescription.length
                                    }
                                    maxLength={1000}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.DESCRIPTION,
                                    )}
                                    className="mt-3"
                                >
                                    {Language.DESCRIPTION}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.DESCRIPTION_HINT}
                                </HintText>

                                <div className="mb-3 p-3 rounded border bg-light">
                                    <h6>
                                        <strong>
                                            {
                                                Language.HOWTO_USE_DESCRIPTION_PLACEHOLDER
                                            }
                                            :
                                        </strong>
                                    </h6>
                                    <ul>
                                        <li>
                                            <code>{Language.CTA_BANNER}</code>
                                            {Language.CTA_BANNER_HINT}
                                        </li>
                                        <li>
                                            <code>
                                                {Language.DOCUMENT_STEPS}
                                            </code>
                                            {Language.DOCUMENT_STEPS_HINT}
                                        </li>
                                    </ul>
                                    <p>{Language.PLACE_PLACEHOLDERS}</p>

                                    <div className="row mt-3">
                                        <div className="col-md-6 mb-2">
                                            <h6 className="mb-2">
                                                Placeholder:{' '}
                                                <code>
                                                    {Language.CTA_BANNER}
                                                </code>
                                            </h6>
                                            <img
                                                src={CTABox}
                                                style={{ objectFit: 'contain' }}
                                                alt="CTA Banner Example"
                                                className="img-fluid rounded border"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <h6 className="mb-2">
                                                Placeholder:{' '}
                                                <code>
                                                    {' '}
                                                    {Language.DOCUMENT_STEPS}
                                                </code>
                                            </h6>
                                            <img
                                                src={HowTo}
                                                style={{ objectFit: 'contain' }}
                                                alt="Document Steps Example"
                                                className="img-fluid rounded border"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div id="description">
                                    <JoditEditor
                                        ref={editor}
                                        value={formData.description}
                                        config={Editorconfig}
                                        onBlur={(newContent) =>
                                            setFormData({
                                                ...formData,
                                                description: newContent,
                                            })
                                        }
                                    />
                                </div>
                                {errors.description && (
                                    <div className="alert alert-danger">
                                        {errors.description}
                                    </div>
                                )}

                                <FAQs
                                    folderName={folderName}
                                    faqValidationError={errors.faqs}
                                    setLoader={(status: boolean) =>
                                        setShowLoader(status)
                                    }
                                    setUniqueFolderName={(name: string) =>
                                        setFolderName(name)
                                    }
                                    showFaqNote={showFaqNote}
                                    setShowFaqNote={(status: boolean) =>
                                        setShowFaqNote(status)
                                    }
                                />

                                <div className="d-flex justify-content-end">
                                    <CButton
                                        color="success"
                                        type="submit"
                                        className="mt-4"
                                    >
                                        {isEditDocument
                                            ? Language.UPDATE_DOCUMENT
                                            : Language.SAVE_DOCUMENT}
                                    </CButton>
                                </div>
                            </CForm>
                        </CCardBody>
                    </CCard>
                </>
            )}
        </Layout>
    );
};

export default DocumentTemplateTranslation;
